import { useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { traAdmin, type TraWaitlistEntry, type TraWaitlistStatus } from '@/lib/api/traAdminClient';
import { COUNTRIES } from '@alascorporation/utils';
import { TraWaitlistStatusBadge } from '@/components/tra/TraWaitlistStatusBadge';
import ModuleLayout from '../../components/admin/ModuleLayout';
import { textareaCls, btnSave, btnSecondary, StyledSelect } from '../../components/admin/FormComponents';
import { toast, toastError, toastSuccess } from '../../lib/toast';
import { applyOptimistic, rollbackOptimistic } from '../../lib/optimisticMutation';
import InlineSavingChip from '../../components/ui/badge/InlineSavingChip';
import { LoadState } from "@/components/ui/LoadState"

const STATUS_CONFIG: Record<TraWaitlistStatus, { label: string; activeCls: string }> = {
  pending: { label: 'Pendiente', activeCls: 'bg-gray-700 text-white' },
  selected: { label: 'Seleccionado', activeCls: 'bg-success-500 text-white' },
  waitlisted: { label: 'En espera', activeCls: 'bg-warning-600 text-white' },
  rejected: { label: 'Rechazado', activeCls: 'bg-error-600 text-white' },
};

const STATUS_ORDER: TraWaitlistStatus[] = ['pending', 'selected', 'waitlisted', 'rejected'];

const INACTIVE_CLS =
  'border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.04]';

function IdCardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="8.5" cy="12" r="1.8" stroke="currentColor" strokeWidth="1.5" />
      <path d="M13 10h5M13 14h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ContactIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 6.5l8 5.5 8-5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function AcademicIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 4l9 4.5-9 4.5-9-4.5L12 4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M6 10.5V16c0 1 2.5 2.5 6 2.5s6-1.5 6-2.5v-5.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function RocketIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3c2.5 1.6 4 4.4 4 8 0 2-1 4-2 5l-2 2-2-2c-1-1-2-3-2-5 0-3.6 1.5-6.4 4-8z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="9" r="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9 14l-2.5 4M15 14l2.5 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SectionCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
      <div className="mb-4 flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300">
          {icon}
        </span>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-gray-800 dark:text-white/90">{value}</p>
    </div>
  );
}

export default function WaitlistDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [notesOverride, setNotesOverride] = useState<string | null>(null);
  const [savingNotes, setSavingNotes] = useState(false);

  const { data: entry, isLoading } = useQuery({
    queryKey: ['tra-waitlist', id],
    queryFn: () => traAdmin.getWaitlistEntry(id!),
    enabled: !!id,
  });

  const { data: editionsData } = useQuery({
    queryKey: ['tra-editions-list'],
    queryFn: () => traAdmin.listEditions({ limit: 50, page: 1 }),
  });
  const editions = editionsData?.items ?? [];

  // Cambio de estado: aplica de inmediato a la caché (mismo frame) y solo
  // revierte si el servidor rechaza — ver design.md, receta "Toggle de detalle".
  const statusMutation = useMutation({
    mutationFn: (status: TraWaitlistStatus) =>
      traAdmin.updateWaitlistStatus(entry!.id, { status, reviewNotes: entry!.reviewNotes ?? null }),
    onMutate: async (status) => {
      const previous = await applyOptimistic<TraWaitlistEntry>(qc, ['tra-waitlist', id], (prev) =>
        prev ? { ...prev, status } : prev,
      );
      return { previous };
    },
    onError: (_err, _status, context) => {
      rollbackOptimistic(qc, ['tra-waitlist', id], context?.previous);
      toastError('No se pudo guardar la decisión. Intenta de nuevo.');
    },
    onSuccess: (updated) => {
      qc.setQueryData(['tra-waitlist', id], updated);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['tra-waitlist'] });
      qc.invalidateQueries({ queryKey: ['server-table', '/tra/certification-waitlist'] });
    },
  });

  // tra-waitlist-edition-reassignment: mismo "recipe" que statusMutation —
  // cambia editionId (o lo limpia con null) y resetea status a "pending" de
  // inmediato en la caché, revierte si el servidor rechaza.
  const editionMutation = useMutation({
    mutationFn: (editionId: string | null) => traAdmin.reassignWaitlistEdition(entry!.id, editionId),
    onMutate: async (editionId) => {
      const previous = await applyOptimistic<TraWaitlistEntry>(qc, ['tra-waitlist', id], (prev) =>
        prev ? { ...prev, editionId, status: 'pending' } : prev,
      );
      return { previous };
    },
    onError: (_err, _editionId, context) => {
      rollbackOptimistic(qc, ['tra-waitlist', id], context?.previous);
      toastError('No se pudo reasignar la edición. Intenta de nuevo.');
    },
    onSuccess: (updated) => {
      qc.setQueryData(['tra-waitlist', id], updated);
      if (updated.revokedPreviousEnrollment) {
        toast.info('Se revocó su acceso al semillero de la cohorte anterior.');
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['tra-waitlist'] });
      qc.invalidateQueries({ queryKey: ['server-table', '/tra/certification-waitlist'] });
      qc.invalidateQueries({ queryKey: ['tra-editions-list'] });
    },
  });

  if (isLoading) return <LoadState />;
  if (!entry) return <div className="p-6 text-gray-400">Solicitud no encontrada.</div>;

  const currentStatus = entry.status;
  const currentNotes = notesOverride ?? (entry.reviewNotes ?? '');
  const hasUnsavedNotes = notesOverride !== null && notesOverride !== (entry.reviewNotes ?? '');

  const statusChipStatus = statusMutation.isPending ? 'saving' : statusMutation.isError ? 'error' : 'idle';
  const retryStatus = () => {
    if (statusMutation.variables !== undefined) statusMutation.mutate(statusMutation.variables);
  };

  const editionChipStatus = editionMutation.isPending ? 'saving' : editionMutation.isError ? 'error' : 'idle';
  const retryEdition = () => {
    if (editionMutation.variables !== undefined) editionMutation.mutate(editionMutation.variables);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const updated = await traAdmin.updateWaitlistStatus(entry.id, {
        status: entry.status,
        reviewNotes: currentNotes || null,
      });
      qc.setQueryData(['tra-waitlist', id], updated);
      setNotesOverride(null);
      toastSuccess('Las notas se guardaron correctamente.');
      qc.invalidateQueries({ queryKey: ['tra-waitlist'] });
      qc.invalidateQueries({ queryKey: ['server-table', '/tra/certification-waitlist'] });
    } catch {
      toastError('No se pudieron guardar las notas. Intenta de nuevo.');
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: 'SateLab', to: '/' },
        'TRA',
        { label: 'Lista de espera', to: '/tra/waitlist' },
        entry.fullName,
      ]}
      title={entry.fullName}
      subtitle={`Solicitud de certificación L1 · registrada el ${new Date(entry.createdAt).toLocaleDateString('es-CO')}`}
      tabs={[
        { label: 'Lista de espera', to: '/tra/waitlist' },
        { label: 'Ediciones', to: '/tra/editions' },
      ]}
      metrics={[]}
      actions={
        <>
          <button type="button" onClick={() => navigate('/tra/waitlist')} className={btnSecondary}>
            ← Volver
          </button>
          <TraWaitlistStatusBadge status={entry.status} />
        </>
      }
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-4">
          <SectionCard icon={<IdCardIcon />} title="Identificación">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Cédula" value={entry.cedula} />
              <Field label="Fecha de nacimiento" value={new Date(entry.dateOfBirth).toLocaleDateString('es-CO')} />
            </div>
          </SectionCard>

          <SectionCard icon={<ContactIcon />} title="Contacto">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Correo electrónico" value={entry.email} />
              <Field label="Teléfono" value={entry.phone} />
              <Field label="Dirección" value={entry.address} />
              <Field label="País" value={COUNTRIES.find((c) => c.value === entry.countryCode)?.label ?? entry.countryCode ?? '—'} />
              <Field label="Departamento / Estado" value={entry.stateProvince ?? '—'} />
              <Field label="Ciudad" value={entry.city} />
            </div>
          </SectionCard>

          <SectionCard icon={<AcademicIcon />} title="Formación académica">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Institución educativa" value={entry.educationalInstitution} />
              <Field label="Título o carrera" value={entry.degreeOrProgram} />
            </div>
          </SectionCard>

          <SectionCard icon={<RocketIcon />} title="Experiencia en cohetería y motivación">
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Experiencia previa</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800 dark:text-white/90">
                  {entry.rocketryExperience || 'Sin información.'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Motivación</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800 dark:text-white/90">{entry.motivation}</p>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Decision sidebar */}
        <div className="w-full lg:w-80 shrink-0 lg:sticky lg:top-6 space-y-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Decisión</h3>
              <InlineSavingChip status={statusChipStatus} onRetry={retryStatus} />
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => statusMutation.mutate(s)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    currentStatus === s ? STATUS_CONFIG[s].activeCls : INACTIVE_CLS
                  }`}
                >
                  {STATUS_CONFIG[s].label}
                </button>
              ))}
            </div>

            <label className="mt-4 mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Notas de revisión
            </label>
            <textarea
              className={textareaCls}
              placeholder="Opcional"
              rows={4}
              value={currentNotes}
              onChange={(e) => setNotesOverride(e.target.value)}
            />

            {entry.reviewedAt && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Última revisión: {new Date(entry.reviewedAt).toLocaleString('es-CO')}
              </p>
            )}

            <div className="mt-4 flex items-center justify-between gap-2">
              {hasUnsavedNotes ? (
                <span className="text-xs font-medium text-warning-700">Notas sin guardar</span>
              ) : (
                <span />
              )}
              <button type="button" disabled={savingNotes || !hasUnsavedNotes} onClick={handleSaveNotes} className={btnSave}>
                {savingNotes ? 'Guardando...' : 'Guardar notas'}
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Edición asignada</h3>
              <InlineSavingChip status={editionChipStatus} onRetry={retryEdition} />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Cambiarla revoca el acceso al semillero de la edición anterior y regresa a "Pendiente".
            </p>
            <StyledSelect
              value={entry.editionId ?? ''}
              onChange={(e) => editionMutation.mutate(e.target.value || null)}
            >
              <option value="">Ninguna (quitar de edición)</option>
              {editions.map((ed) => (
                <option key={ed.id} value={ed.id}>{ed.name}</option>
              ))}
            </StyledSelect>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Otros datos</h3>
            <div className="mt-3 space-y-3">
              <Field label="Referido por" value={entry.referredBy ?? '—'} />
              <Field label="Miembro TRA existente" value={entry.isExistingTraMember ? 'Sí' : 'No'} />
              {entry.isExistingTraMember && (
                <Field label="Número de miembro TRA" value={entry.traMemberNumber ?? '—'} />
              )}
              <Field label="Registrado" value={new Date(entry.createdAt).toLocaleString('es-CO')} />
            </div>
          </div>
        </div>
      </div>
    </ModuleLayout>
  );
}
